import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, CheckCircle, ArrowLeft } from 'lucide-react';
import { validateEmail, validatePhone } from '../lib/validation';
import { Link } from 'wouter';
import { Checkbox } from '../components/ui/checkbox';

function useContactSales() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('contact') === 'sales';
}

export default function ScheduleDemoPage() {
  const isContactSales = useContactSales();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!firstName.trim()) nextErrors.firstName = 'First name is required';
    if (!lastName.trim()) nextErrors.lastName = 'Last name is required';
    if (!companyName.trim()) nextErrors.companyName = 'Company name is required';

    const phoneResult = validatePhone(phone);
    if (!phoneResult.valid && phone.trim()) {
      nextErrors.phone = phoneResult.error || 'Invalid phone number';
    } else if (!phone.trim()) {
      nextErrors.phone = 'Phone number is required';
    }

    const emailResult = validateEmail(email);
    if (!emailResult.valid && email.trim()) {
      nextErrors.email = emailResult.error || 'Invalid email';
    } else if (!email.trim()) {
      nextErrors.email = 'Email is required';
    }

    if (!smsConsent) {
      nextErrors.smsConsent = 'You must agree to receive SMS messages.';
    }

    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/demo-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          companyName: companyName.trim(),
          phone: phone.trim(),
          email: email.trim().toLowerCase(),
          requestType: isContactSales ? 'sales' : 'demo',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data?.error || 'Something went wrong. Please try again.');
        return;
      }
      setIsSubmitted(true);
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="container mx-auto px-4 max-w-5xl">
          <Card className="w-full max-w-md mx-auto bg-white border border-slate-200 shadow-xl rounded-2xl ring-1 ring-slate-200/50">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center ring-2 ring-emerald-400/30">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">Request received</CardTitle>
              <CardDescription className="text-slate-600">
                Thanks, {firstName}. We'll reach out soon{isContactSales ? ' to discuss your needs.' : ' to schedule your demo.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button asChild variant="outline" className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 bg-white">
                <Link href="/">Back to home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Helmet>
        <title>Schedule a Demo – Remodra</title>
        <meta name="description" content="Book a demo of Remodra construction management software. See how contractors manage estimates, invoices, and projects." />
        <link rel="canonical" href="https://getremodra.com/schedule-demo" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://getremodra.com/schedule-demo" />
        <meta property="og:title" content="Schedule a Demo – Remodra" />
        <meta property="og:description" content="Book a demo of Remodra construction management software. See how contractors manage estimates, invoices, and projects." />
        <meta property="og:image" content="https://getremodra.com/remodra-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://getremodra.com/schedule-demo" />
        <meta name="twitter:title" content="Schedule a Demo – Remodra" />
        <meta name="twitter:description" content="Book a demo of Remodra construction management software. See how contractors manage estimates, invoices, and projects." />
        <meta name="twitter:image" content="https://getremodra.com/remodra-logo.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://getremodra.com/" },
              { "@type": "ListItem", position: 2, name: "Schedule a Demo" }
            ]
          })}
        </script>
      </Helmet>
      <div className="container mx-auto px-4 max-w-5xl">
        <Card className="w-full max-w-md mx-auto bg-white border border-slate-200 shadow-xl rounded-2xl ring-1 ring-slate-200/50 overflow-hidden">
          <CardHeader className="text-center border-b border-slate-100 bg-slate-50/50">
            <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              {isContactSales ? 'Contact Sales' : 'Schedule a demo'}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {isContactSales
                ? "Share your details and we'll get in touch to discuss your needs."
                : "Share your details and we'll get in touch to set up your demo."}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-slate-800 font-medium">First name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); setErrors((prev) => ({ ...prev, firstName: '' })); }}
                    placeholder="John"
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  {errors.firstName && <p className="text-xs text-red-600">{errors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-slate-800 font-medium">Last name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => { setLastName(e.target.value); setErrors((prev) => ({ ...prev, lastName: '' })); }}
                    placeholder="Smith"
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  {errors.lastName && <p className="text-xs text-red-600">{errors.lastName}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-slate-800 font-medium">Company name</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => { setCompanyName(e.target.value); setErrors((prev) => ({ ...prev, companyName: '' })); }}
                  placeholder="Acme Inc."
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {errors.companyName && <p className="text-xs text-red-600">{errors.companyName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-800 font-medium">Phone number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setErrors((prev) => ({ ...prev, phone: '' })); }}
                  placeholder="(555) 123-4567"
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-800 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: '' })); }}
                  placeholder="you@company.com"
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="smsConsent"
                    checked={smsConsent}
                    onCheckedChange={(checked) => {
                      setSmsConsent(Boolean(checked));
                      setErrors((prev) => ({ ...prev, smsConsent: '' }));
                    }}
                    className="mt-1 border-slate-300 data-[state=checked]:bg-emerald-600"
                  />
                  <Label htmlFor="smsConsent" className="text-xs text-slate-700 leading-snug font-normal">
                    By submitting this form, you agree to receive SMS text messages from Remodra about your demo request and product updates.
                    Message frequency varies. Message and data rates may apply. Reply STOP to cancel anytime. Reply HELP for support.
                    View our{' '}
                    <Link href="/privacy" className="text-emerald-600 hover:text-emerald-700 underline">
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link href="/terms" className="text-emerald-600 hover:text-emerald-700 underline">
                      Terms &amp; Conditions
                    </Link>.
                  </Label>
                </div>
                {errors.smsConsent && <p className="text-xs text-red-600">{errors.smsConsent}</p>}
              </div>
              {submitError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold hover:from-emerald-700 hover:to-teal-700 shadow-lg shadow-emerald-500/25 rounded-xl"
              >
                {isSubmitting ? 'Sending…' : isContactSales ? 'Contact sales' : 'Request demo'}
              </Button>
            </form>
            <p className="text-sm text-slate-500 text-center mt-6">
              <Link href="/" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Back to home
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
